import React, { useState, useEffect } from "react";
import { Phone, Menu, X } from "lucide-react";
import { useContent } from "../ContentContext";

const links = [
  { label: "About", id: "about" },
  { label: "Services", id: "services" },
  { label: "Gallery", id: "gallery" },
  { label: "Areas", id: "areas" },
  { label: "Reviews", id: "reviews" },
  { label: "Blog", id: "blog" },
  { label: "FAQ", id: "faq" },
  { label: "Contact", id: "contact" },
];

export default function Nav({ onEstimate }) {
  const { company } = useContent();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  const go = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="main-nav"
      className="fixed top-0 left-0 w-full z-50 transition-all duration-300"
      style={{
        background: scrolled ? "rgba(250,249,246,0.85)" : "rgba(10,31,22,0.35)",
        backdropFilter: "blur(18px)",
        borderBottom: scrolled ? "1px solid rgba(0,0,0,0.06)" : "1px solid rgba(240,235,225,0.12)",
      }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-[68px] flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-2.5 text-left"
        >
          {company.logo && (
            <img src={company.logo} alt={`${company.name} logo`} className="h-10 w-auto shrink-0"
                 style={{ filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.28))" }} />
          )}
          <span className="font-display font-extrabold text-lg leading-none tracking-tight hidden sm:inline"
                style={{ color: scrolled ? "var(--pine)" : "var(--moss)" }}>
            TOP OF THE LINE
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <button key={l.id} data-testid={`nav-${l.id}`} onClick={() => go(l.id)}
                    className="text-sm font-medium transition-opacity hover:opacity-60"
                    style={{ color: scrolled ? "var(--charcoal)" : "var(--moss)" }}>
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a href={`tel:${company.phoneRaw}`} data-testid="nav-call"
             className="hidden md:flex items-center gap-2 text-sm font-semibold"
             style={{ color: scrolled ? "var(--pine)" : "var(--moss)" }}>
            <Phone size={16} /> {company.phone}
          </a>
          <button data-testid="nav-estimate-btn" onClick={onEstimate}
                  className="hidden sm:inline-flex rounded-full px-5 py-2.5 text-sm font-semibold transition-transform hover:scale-105"
                  style={{ background: "var(--sun)", color: "var(--pine)" }}>
            Request a Free Estimate
          </button>
          <button data-testid="nav-mobile-toggle" className="lg:hidden p-2" onClick={() => setOpen(!open)}
                  style={{ color: scrolled ? "var(--pine)" : "var(--moss)" }}>
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden px-6 pb-6 pt-2" style={{ background: "rgba(250,249,246,0.98)" }}>
          {links.map((l) => (
            <button key={l.id} data-testid={`nav-mobile-${l.id}`} onClick={() => go(l.id)}
                    className="block w-full text-left py-3 border-b border-black/5 font-medium"
                    style={{ color: "var(--charcoal)" }}>
              {l.label}
            </button>
          ))}
          <button data-testid="nav-mobile-estimate" onClick={() => { setOpen(false); onEstimate(); }}
                  className="mt-4 w-full rounded-full py-3 font-semibold"
                  style={{ background: "var(--sun)", color: "var(--pine)" }}>
            Request a Free Estimate
          </button>
        </div>
      )}
    </header>
  );
}
